import Typography from "antd/lib/typography";
import React, { Fragment, useContext, useEffect } from "react";
import Helmet from "react-helmet";
import LayoutContext from "~context/Layout";

function DisclaimerPage(): JSX.Element {
    const layout = useContext(LayoutContext);

    useEffect(() => {
        if (layout.data && layout.actions) {
            if (layout.data.header !== true) layout.actions.updateHeader(true);
            if (layout.data.footer !== true) layout.actions.updateFooter(true);
        }
    }, [layout.actions, layout.data]);

    return (
        <Fragment>
            <Helmet>
                <title>Disclaimer - Twenty4 Tan Co.</title>
            </Helmet>

            <div className="container-xl my-50 px-50">
                <Typography.Title level={1}>Disclaimer</Typography.Title>
                <Typography.Paragraph>
                    Last updated: August 17, 2021
                </Typography.Paragraph>
                <Typography.Title level={1}>
                    Interpretation and Definitions
                </Typography.Title>
                <Typography.Title level={2}>Interpretation</Typography.Title>
                <Typography.Paragraph>
                    The words of which the initial letter is capitalized have
                    meanings defined under the following conditions. The
                    following definitions shall have the same meaning regardless
                    of whether they appear in singular or in plural.
                </Typography.Paragraph>
                <Typography.Title level={2}>Definitions</Typography.Title>
                <Typography.Paragraph>
                    For the purposes of this Disclaimer:
                </Typography.Paragraph>
                <ul>
                    <li>
                        <Typography.Text strong={true}>Company</Typography.Text>{" "}
                        (referred to as either &quot;the Company&quot;,
                        &quot;We&quot;, &quot;Us&quot; or &quot;Our&quot; in
                        this Disclaimer) refers to Example LLC, 1 Cupertino, CA
                        95014.
                    </li>
                    <li>
                        <Typography.Text strong={true}>Service</Typography.Text>{" "}
                        refers to the Website.
                    </li>
                    <li>
                        <Typography.Text strong={true}>You</Typography.Text>{" "}
                        means the individual accessing the Service, or the
                        company, or other legal entity on behalf of which such
                        individual is accessing or using the Service, as
                        applicable.
                    </li>
                    <li>
                        <Typography.Text strong={true}>Website</Typography.Text>{" "}
                        refers to Example Site, accessible from{" "}
                        <Typography.Link
                            href="https://twenty4tanco.com"
                            rel="external nofollow noopener noreferrer"
                            target="_blank"
                        >
                            https://twenty4tanco.com
                        </Typography.Link>
                    </li>
                </ul>
                <Typography.Title level={1}>Disclaimer</Typography.Title>
                <Typography.Paragraph>
                    The information contained on the Service is for general
                    information purposes only.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    The Company assumes no responsibility for errors or
                    omissions in the contents of the Service.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    In no event shall the Company be liable for any special,
                    direct, indirect, consequential, or incidental damages or
                    any damages whatsoever, whether in an action of contract,
                    negligence or other tort, arising out of or in connection
                    with the use of the Service or the contents of the Service.
                    The Company reserves the right to make additions, deletions,
                    or modifications to the contents on the Service at any time
                    without prior notice. This Disclaimer has been created with
                    the help of the{" "}
                    <Typography.Link
                        href="https://www.termsfeed.com/disclaimer-generator/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Disclaimer Generator
                    </Typography.Link>
                    .
                </Typography.Paragraph>
                <Typography.Paragraph>
                    The Company does not warrant that the Service is free of
                    viruses or other harmful components.
                </Typography.Paragraph>
                <Typography.Title level={1}>
                    External Links Disclaimer
                </Typography.Title>
                <Typography.Paragraph>
                    The Service may contain links to external websites that are
                    not provided or maintained by or in any way affiliated with
                    the Company.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    Please note that the Company does not guarantee the
                    accuracy, relevance, timeliness, or completeness of any
                    information on these external websites.
                </Typography.Paragraph>
                <Typography.Title level={1}>
                    Errors and Omissions Disclaimer
                </Typography.Title>
                <Typography.Paragraph>
                    The information given by the Service is for general guidance
                    on matters of interest only. Even if the Company takes every
                    precaution to insure that the content of the Service is both
                    current and accurate, errors can occur. Plus, given the
                    changing nature of laws, rules and regulations, there may be
                    delays, omissions or inaccuracies in the information
                    contained on the Service.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    The Company is not responsible for any errors or omissions,
                    or for the results obtained from the use of this
                    information.
                </Typography.Paragraph>
                <Typography.Title level={1}>
                    Fair Use Disclaimer
                </Typography.Title>
                <Typography.Paragraph>
                    The Company may use copyrighted material which has not
                    always been specifically authorized by the copyright owner.
                    The Company is making such material available for criticism,
                    comment, news reporting, teaching, scholarship, or research.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    The Company believes this constitutes a &quot;fair use&quot;
                    of any such copyrighted material as provided for in section
                    107 of the United States Copyright law.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    If You wish to use copyrighted material from the Service for
                    your own purposes that go beyond fair use, You must obtain
                    permission from the copyright owner.
                </Typography.Paragraph>
                <Typography.Title level={1}>
                    Views Expressed Disclaimer
                </Typography.Title>
                <Typography.Paragraph>
                    The Service may contain views and opinions which are those
                    of the authors and do not necessarily reflect the official
                    policy or position of any other author, agency,
                    organization, employer or company, including the Company.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    Comments published by users are their sole responsibility
                    and the users will take full responsibility, liability and
                    blame for any libel or litigation that results from
                    something written in or as a direct result of something
                    written in a comment. The Company is not liable for any
                    comment published by users and reserve the right to delete
                    any comment for any reason whatsoever.
                </Typography.Paragraph>
                <Typography.Title level={1}>
                    No Responsibility Disclaimer
                </Typography.Title>
                <Typography.Paragraph>
                    The information on the Service is provided with the
                    understanding that the Company is not herein engaged in
                    rendering legal, accounting, tax, or other professional
                    advice and services. As such, it should not be used as a
                    substitute for consultation with professional accounting,
                    tax, legal or other competent advisers.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    In no event shall the Company or its suppliers be liable for
                    any special, incidental, indirect, or consequential damages
                    whatsoever arising out of or in connection with your access
                    or use or inability to access or use the Service.
                </Typography.Paragraph>
                <Typography.Title level={1}>
                    &quot;Use at Your Own Risk&quot; Disclaimer
                </Typography.Title>
                <Typography.Paragraph>
                    All information in the Service is provided &quot;as
                    is&quot;, with no guarantee of completeness, accuracy,
                    timeliness or of the results obtained from the use of this
                    information, and without warranty of any kind, express or
                    implied, including, but not limited to warranties of
                    performance, merchantability and fitness for a particular
                    purpose.
                </Typography.Paragraph>
                <Typography.Paragraph>
                    The Company will not be liable to You or anyone else for any
                    decision made or action taken in reliance on the information
                    given by the Service or for any consequential, special or
                    similar damages, even if advised of the possibility of such
                    damages.
                </Typography.Paragraph>
                <Typography.Title level={1}>Contact Us</Typography.Title>
                <Typography.Paragraph>
                    If you have any questions about this Disclaimer, You can
                    contact Us:
                </Typography.Paragraph>
                <ul>
                    <li>
                        <Typography.Paragraph>
                            By email:{" "}
                            <Typography.Link
                                href="mailto:twenty4tanco@gmail.com"
                                rel="external nofollow noopener noreferrer"
                                target="_blank"
                            >
                                twenty4tanco@gmail.com
                            </Typography.Link>
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            By visiting this page on our website:{" "}
                            <Typography.Link
                                href="https://twenty4tanco.com/contact"
                                rel="external nofollow noopener noreferrer"
                                target="_blank"
                            >
                                https://twenty4tanco.com/contact
                            </Typography.Link>
                        </Typography.Paragraph>
                    </li>
                    <li>
                        <Typography.Paragraph>
                            By phone number:{" "}
                            <Typography.Link
                                href="tel:+15736827146"
                                rel="external nofollow noopener noreferrer"
                            >
                                +1 (573) 682-7146
                            </Typography.Link>
                        </Typography.Paragraph>
                    </li>
                </ul>
            </div>
        </Fragment>
    );
}

export default DisclaimerPage;
